export type ReaderMode = 'frank' | 'original';
export type Theme = 'light' | 'sepia' | 'dark';
export type AnnotationCategory = 'important' | 'apply' | 'question';

export interface SourceRef {
  pdfPageIndex: number;
  pdfPageNumber: number;
  printedPageLabel: string | null;
  bbox?: [number, number, number, number];
}

export interface ContentBlock {
  semanticTable?: {layout?:'compact'|'parameters'|'classification';headerRows?:{text:string;colSpan?:number;rowSpan?:number;header?:boolean}[][];complexRows?:{text:string;colSpan?:number;rowSpan?:number;header?:boolean}[][];caption:string;headers:string[];rows:string[][];notes?:string[];status:string};
  listLevel?:number;
  parentItemId?:string|null;
  glosses?: Gloss[];
  id: string;
  documentId: string;
  sectionId: string;
  kind: 'heading' | 'paragraph' | 'list-item' | 'page-marker' | 'table' | 'figure' | 'footnote' | 'bibliography';
  order: number;
  enText: string;
  ruText: string;
  sourceRefs: SourceRef[];
  reviewStatus: string;
  translationStatus?: string;
  editorialNoteRu?: string;
  adaptationStatus?: string;
  revision: number;
  textHash: string;
}

export interface Gloss { id:string; blockId:string; start:number; end:number; sourceTextHash:string; textRu:string; kind:'translation'|'context'|'grammar'; reviewStatus:string; }

export interface ContentSection {
  id: string;
  documentId: string;
  parentId: string | null;
  number: string;
  titleEn: string;
  titleRu: string;
  order: number;
  pdfPageFrom: number;
  pdfPageTo: number;
  blockIds: string[];
  editorialContext?: string;
  editorialStatus?: string;
}

export interface DocumentMeta {
  id: string;
  titleEn: string;
  titleRu: string;
  publisher: string;
  series: string;
  year: number | null;
  sourceSha256: string;
  pdfPageCount: number;
  contentVersion: string;
  languages: string[];
  sourceFile: string;
  sourceVerification: string;
}

export interface ContentPackage {
  migration?: {fromVersion:string;toVersion:string;mappings:{oldId:string;newIds:string[];status:string}[]};
  formatId: string;
  schemaVersion: number;
  contentVersion: string;
  glossaryVersion: string;
  adaptationVersion: string;
  generatedAt: string;
  sourceStatus: string;
  document: DocumentMeta;
  sections: ContentSection[];
  blocks: ContentBlock[];
  glosses: unknown[];
  assets: unknown[];
  notes: string[];
}

export interface Anchor {
  fragments?: Omit<Anchor, 'fragments'>[];
  status?: 'resolved' | 'orphaned';
  blockId: string;
  language: 'en' | 'ru';
  start: number;
  end: number;
  exactQuote: string;
  prefix: string;
  suffix: string;
  textHash: string;
}

export interface Annotation {
  contextQuote?: string;
  translationLabel?: string;
  id: string;
  documentId: string;
  contentVersion: string;
  anchor: Anchor;
  quoteEn: string;
  quoteRu: string;
  kind: 'highlight' | 'note';
  category: AnnotationCategory;
  comment: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  revision: number;
  deletedAt: string | null;
  sourceRefs: SourceRef[];
}

export interface Bookmark {
  status?: 'resolved'|'orphaned';
  id: string;
  documentId: string;
  contentVersion: string;
  blockId: string;
  pageNumber: number;
  createdAt: string;
}

export interface VocabularyItem {
  id: string;
  documentId: string;
  contentVersion: string;
  expression: string;
  meaningRu: string;
  contextQuote: string;
  anchor: Anchor;
  userEdited: boolean;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  dueAt: string;
  reviewStage: number;
  lastReviewedAt: string | null;
  lastResult: 'remember' | 'repeat' | null;
}

export interface ReviewEvent {
  id: string;
  sessionId: string;
  vocabularyItemId: string;
  result: 'remember' | 'repeat';
  timestamp: string;
}

export interface ReadingPosition {
  documentId: string;
  sectionId: string;
  pageNumber: number;
  blockId: string | null;
  offset: number;
  representation: ReaderMode;
  contentVersion: string;
  updatedAt: string;
}

export interface ReadingProgress {
  visitedSectionIds: string[];
  explicitCompletedSectionIds: string[];
}

export interface Settings {
  theme: Theme;
  fontSize: number;
  lineHeight: number;
  serif: boolean;
  mode: ReaderMode;
  repeatOriginal: boolean;
  keepAwakePreference: boolean;
}

export interface BackupStatus {
  lastGeneratedAt: string | null;
  lastSharedAt: string | null;
  lastExportedRevision: number;
}

export interface UserState {
  pendingDraft?: SelectionDraft & {category:AnnotationCategory;comment:string};
  importRecords?: {id:string;createdAt:string;mode:'merge'|'replace';annotations:number;vocabulary:number}[];
  reviewSession?: {id:string;queue:string[];index:number;startedAt:string};
  schemaVersion: number;
  revision: number;
  readingPosition: ReadingPosition;
  readingProgress: ReadingProgress;
  settings: Settings;
  annotations: Annotation[];
  bookmarks: Bookmark[];
  vocabulary: VocabularyItem[];
  reviewEvents: ReviewEvent[];
  backupStatus: BackupStatus;
}

export interface SelectionDraft {
  fragments?: Omit<Anchor, 'fragments'>[];
  block: ContentBlock;
  quote: string;
  language: 'en' | 'ru';
  start: number;
  end: number;
}

export const DOCUMENT_ID = 'iaea-np-t-2.7';

export function defaultUserState(contentVersion = 'candidate-2026-09-15'): UserState {
  return {
    schemaVersion: 1,
    revision: 0,
    readingPosition: {
      documentId: DOCUMENT_ID,
      sectionId: 'section-1',
      pageNumber: 13,
      blockId: null,
      offset: 0,
      representation: 'original',
      contentVersion,
      updatedAt: new Date().toISOString(),
    },
    readingProgress: { visitedSectionIds: ['section-1'], explicitCompletedSectionIds: [] },
    settings: {
      theme: 'light',
      fontSize: 19,
      lineHeight: 1.65,
      serif: true,
      mode: 'original',
      repeatOriginal: false,
      keepAwakePreference: false,
    },
    annotations: [],
    bookmarks: [],
    vocabulary: [],
    reviewEvents: [],
    backupStatus: { lastGeneratedAt: null, lastSharedAt: null, lastExportedRevision: 0 },
  };
}




